import React, { useState, useRef, useEffect } from 'react';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../../common/SafeIcon';

const {
  FiBold, FiItalic, FiUnderline, FiList, FiLink, FiImage, FiCode,
  FiAlignLeft, FiAlignCenter, FiAlignRight, FiType, FiEye, FiEdit3
} = FiIcons;

const RichTextEditor = ({ value, onChange, placeholder = "Start writing..." }) => {
  const [isLinkModalOpen, setIsLinkModalOpen] = useState(false);
  const [linkUrl, setLinkUrl] = useState('');
  const [linkText, setLinkText] = useState('');
  const [isPreviewMode, setIsPreviewMode] = useState(false);
  const editorRef = useRef(null);

  useEffect(() => {
    if (editorRef.current && !isPreviewMode) {
      editorRef.current.innerHTML = value || '';
    }
  }, [value, isPreviewMode]);

  const formatText = (command, value = null) => {
    if (isPreviewMode) return;
    
    document.execCommand(command, false, value);
    editorRef.current.focus();
    handleContentChange();
  };

  const handleContentChange = () => {
    if (isPreviewMode) return;
    
    const content = editorRef.current.innerHTML;
    onChange(content);
  };

  const insertLink = () => {
    if (linkUrl && linkText) {
      const linkHTML = `<a href="${linkUrl}" target="_blank" rel="noopener noreferrer" style="color: #3b82f6; text-decoration: underline;">${linkText}</a>`;
      document.execCommand('insertHTML', false, linkHTML);
      setIsLinkModalOpen(false);
      setLinkUrl('');
      setLinkText('');
      handleContentChange();
    }
  };

  const insertImage = () => {
    const imageUrl = prompt('Enter image URL:');
    if (imageUrl) {
      const imageHTML = `<img src="${imageUrl}" alt="Image" style="max-width: 100%; height: auto; border-radius: 8px; margin: 10px 0;" />`;
      document.execCommand('insertHTML', false, imageHTML);
      handleContentChange();
    }
  };

  const insertHeading = (level) => {
    formatText('formatBlock', `h${level}`);
  };

  const insertCodeBlock = () => {
    const selection = window.getSelection();
    const selectedText = selection.toString();
    const codeHTML = `<pre style="background: #f3f4f6; padding: 12px; border-radius: 8px; margin: 12px 0; font-family: monospace; overflow-x: auto; border: 1px solid #e5e7eb;"><code>${selectedText || 'Your code here'}</code></pre>`;
    document.execCommand('insertHTML', false, codeHTML);
    handleContentChange();
  };

  const formatContent = (content) => {
    if (!content) return '';
    
    // Enhanced content formatting for preview
    return content
      .replace(/\n/g, '<br>')
      .replace(/<h([1-6])>/g, '<h$1 style="font-weight: bold; margin: 16px 0 8px 0; line-height: 1.3;">')
      .replace(/<p>/g, '<p style="margin: 12px 0; line-height: 1.6;">')
      .replace(/<ul>/g, '<ul style="margin: 12px 0; padding-left: 24px;">')
      .replace(/<ol>/g, '<ol style="margin: 12px 0; padding-left: 24px;">')
      .replace(/<li>/g, '<li style="margin: 4px 0;">')
      .replace(/<blockquote>/g, '<blockquote style="border-left: 4px solid #3b82f6; padding-left: 16px; margin: 16px 0; font-style: italic; color: #6b7280;">')
      .replace(/<a /g, '<a style="color: #3b82f6; text-decoration: underline;" ');
  };

  const toolbarButtons = [
    { command: 'bold', icon: FiBold, title: 'Bold' },
    { command: 'italic', icon: FiItalic, title: 'Italic' },
    { command: 'underline', icon: FiUnderline, title: 'Underline' },
    { command: 'separator' },
    { 
      command: 'custom', 
      action: () => insertHeading(1), 
      icon: FiType, 
      title: 'Heading 1',
      label: 'H1'
    },
    { 
      command: 'custom', 
      action: () => insertHeading(2), 
      icon: FiType, 
      title: 'Heading 2',
      label: 'H2'
    },
    { 
      command: 'custom', 
      action: () => insertHeading(3), 
      icon: FiType, 
      title: 'Heading 3',
      label: 'H3'
    },
    { command: 'separator' },
    { command: 'justifyLeft', icon: FiAlignLeft, title: 'Align Left' },
    { command: 'justifyCenter', icon: FiAlignCenter, title: 'Align Center' },
    { command: 'justifyRight', icon: FiAlignRight, title: 'Align Right' },
    { command: 'separator' },
    { command: 'insertUnorderedList', icon: FiList, title: 'Bullet List' },
    { command: 'insertOrderedList', icon: FiList, title: 'Numbered List' },
    { command: 'separator' },
    { command: 'custom', action: insertCodeBlock, icon: FiCode, title: 'Code Block' },
    { command: 'custom', action: () => setIsLinkModalOpen(true), icon: FiLink, title: 'Insert Link' },
    { command: 'custom', action: insertImage, icon: FiImage, title: 'Insert Image' }
  ];

  return (
    <div className="border border-gray-300 rounded-xl overflow-hidden bg-white">
      {/* Toolbar */}
      <div className="bg-gray-50 border-b border-gray-300 p-3 flex items-center justify-between">
        <div className="flex items-center space-x-1 flex-wrap">
          {toolbarButtons.map((button, index) => {
            if (button.command === 'separator') {
              return <div key={index} className="w-px h-6 bg-gray-300 mx-2" />;
            }

            return (
              <button
                key={index}
                type="button"
                onClick={() => {
                  if (button.command === 'custom') {
                    button.action();
                  } else {
                    formatText(button.command, button.value);
                  }
                }}
                disabled={isPreviewMode}
                className={`p-2 text-gray-600 hover:text-gray-900 hover:bg-gray-200 rounded transition-colors relative ${
                  isPreviewMode ? 'opacity-50 cursor-not-allowed' : ''
                }`}
                title={button.title}
              >
                {button.label ? (
                  <span className="text-xs font-bold px-1">{button.label}</span>
                ) : (
                  <SafeIcon icon={button.icon} className="h-4 w-4" />
                )}
              </button>
            );
          })}
        </div>

        {/* Preview Toggle */}
        <div className="flex items-center space-x-2">
          <button
            type="button"
            onClick={() => setIsPreviewMode(!isPreviewMode)}
            className={`flex items-center space-x-2 px-3 py-2 rounded-lg text-sm font-medium transition-all ${
              isPreviewMode
                ? 'bg-blue-100 text-blue-700 border border-blue-300'
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
            <SafeIcon icon={isPreviewMode ? FiEdit3 : FiEye} className="h-4 w-4" />
            <span>{isPreviewMode ? 'Edit' : 'Preview'}</span>
          </button>
        </div>
      </div>

      {/* Editor/Preview */}
      {isPreviewMode ? (
        <div className="min-h-[200px] p-6 bg-white">
          <div 
            className="prose prose-lg max-w-none text-gray-900"
            dangerouslySetInnerHTML={{ __html: formatContent(value) }}
          />
          {!value && (
            <div className="text-gray-400 italic text-center py-12">
              No content to preview. Switch to edit mode to start writing.
            </div>
          )}
        </div>
      ) : (
        <div
          ref={editorRef}
          contentEditable
          onInput={handleContentChange}
          onBlur={handleContentChange}
          className="min-h-[200px] p-6 focus:outline-none text-gray-900 bg-white prose prose-lg max-w-none"
          style={{ 
            minHeight: '200px',
            color: '#111827',
            backgroundColor: '#ffffff'
          }}
          data-placeholder={placeholder}
        />
      )}

      {/* Link Modal */}
      {isLinkModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white border border-gray-300 rounded-xl p-6 w-full max-w-md">
            <h3 className="text-lg font-semibold mb-4 text-gray-900">Insert Link</h3>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Link Text
                </label>
                <input
                  type="text"
                  value={linkText}
                  onChange={(e) => setLinkText(e.target.value)}
                  className="w-full px-3 py-2 bg-white border border-gray-300 rounded-lg text-gray-900 placeholder-gray-500 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="Enter link text"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  URL
                </label>
                <input
                  type="url"
                  value={linkUrl}
                  onChange={(e) => setLinkUrl(e.target.value)}
                  className="w-full px-3 py-2 bg-white border border-gray-300 rounded-lg text-gray-900 placeholder-gray-500 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="https://example.com"
                />
              </div>
            </div>
            <div className="flex space-x-3 mt-6">
              <button
                onClick={insertLink}
                className="flex-1 bg-blue-600 hover:bg-blue-700 text-white py-2 px-4 rounded-lg transition-all duration-300"
              >
                Insert Link
              </button>
              <button
                onClick={() => {
                  setIsLinkModalOpen(false);
                  setLinkUrl('');
                  setLinkText('');
                }}
                className="flex-1 bg-gray-300 hover:bg-gray-400 text-gray-700 py-2 px-4 rounded-lg transition-colors"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      <style jsx>{`
        [contenteditable]:empty:before {
          content: attr(data-placeholder);
          color: #9ca3af;
          font-style: italic;
        }
        
        [contenteditable] * {
          color: inherit !important;
        }
        
        [contenteditable] h1,
        [contenteditable] h2,
        [contenteditable] h3,
        [contenteditable] h4,
        [contenteditable] h5,
        [contenteditable] h6 {
          font-weight: bold;
          margin: 16px 0 8px 0;
          line-height: 1.3;
        }
        
        [contenteditable] h1 { font-size: 2em; }
        [contenteditable] h2 { font-size: 1.5em; }
        [contenteditable] h3 { font-size: 1.17em; }
        
        [contenteditable] p {
          margin: 12px 0;
          line-height: 1.6;
        }
        
        [contenteditable] ul,
        [contenteditable] ol {
          margin: 12px 0;
          padding-left: 24px;
        }
        
        [contenteditable] li {
          margin: 4px 0;
        }
        
        [contenteditable] pre {
          background: #f3f4f6;
          padding: 12px;
          border-radius: 8px;
          margin: 12px 0;
          font-family: monospace;
          overflow-x: auto;
          border: 1px solid #e5e7eb;
        }
        
        [contenteditable] code {
          background: #f3f4f6;
          padding: 2px 6px;
          border-radius: 4px;
          font-family: monospace;
          font-size: 0.9em;
        }
        
        [contenteditable] a {
          color: #3b82f6 !important;
          text-decoration: underline;
        }
        
        [contenteditable] blockquote {
          border-left: 4px solid #3b82f6;
          padding-left: 16px;
          margin: 16px 0;
          font-style: italic;
          color: #6b7280;
        }
        
        [contenteditable] img {
          max-width: 100%;
          height: auto;
          border-radius: 8px;
          margin: 10px 0;
        }
      `}</style>
    </div>
  );
};

export default RichTextEditor;